import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icons.jsx';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import { PLATFORMS, PLATFORM_LABELS } from './shared/platformLabels.js';

export function OverviewTab({ brandForm, publishingForm, platformPrefs = [], socialAccounts = [], strategy, onJumpTo, onAskAI, onConnectPlatform }) {
  const { t } = useTranslation();
  const readiness = buildReadiness({ brandForm, publishingForm, platformPrefs, strategy, t });
  const summary = buildPlanSummary({ brandForm, publishingForm, platformPrefs, strategy, t });
  const channels = connectedChannelSummary(socialAccounts, platformPrefs);
  const nextAction = buildNextAction(readiness.items, strategy, t);

  return (
    <div className="cp-overview-grid">
      <section className="cp-command-card cp-command-card-primary">
        <div className="cp-command-topline">
          <span className="cp-status-pill cp-status-pill-good"><span /> {t('overviewTab.autopilotContext')}</span>
          <span className="cp-score-ring" style={{ '--score': `${readiness.score}%` }}>{readiness.score}%</span>
        </div>
        <div className="cp-next-action">
          <span>{nextAction.label}</span>
          <h2>{nextAction.title}</h2>
          <p>{summary}</p>
        </div>
        <div className="cp-command-actions">
          <button className="btn btn-primary" onClick={() => onJumpTo?.(nextAction.tab)}>
            <Icon name={nextAction.icon} size={13} /> {nextAction.cta}
          </button>
          <button className="btn btn-secondary" onClick={() => onAskAI?.({ scope: 'content-plan.overview' })}>
            <Icon name="sparkles" size={13} /> {t('overviewTab.askAiToReview')}
          </button>
        </div>
      </section>

      <section className="cp-system-map" aria-label={t('overviewTab.systemMapAriaLabel')}>
        {readiness.items.map((item) => (
          <button key={item.id} className="cp-system-node" onClick={() => onJumpTo?.(item.tab)}>
            <span className={'cp-node-dot ' + (item.ready ? 'is-ready' : '')} />
            <span className="cp-node-copy">
              <strong>{item.label}</strong>
              <span>{item.description}</span>
            </span>
            <span className="cp-node-status">{item.ready ? t('overviewTab.statusReady') : item.status || t('overviewTab.statusNeedsInput')}</span>
          </button>
        ))}
      </section>

      <section className="cp-command-card">
        <div className="cp-panel-title-row">
          <div>
            <div className="settings-section-label">{t('overviewTab.monthPlanLabel')}</div>
            <h3>{t('overviewTab.monthAtAGlance')}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => onJumpTo?.('month')}>
            {t('overviewTab.manage')} <Icon name="arrow_r" size={12} />
          </button>
        </div>
        <div className="cp-mini-month">
          {[1, 2, 3, 4].map((week, i) => (
            <div key={week} className={'cp-mini-week ' + (i === 0 && strategy ? 'is-ready' : '')}>
              <span>{t('overviewTab.week', { week })}</span>
              <strong>{i === 0 && strategy ? t('overviewTab.readyToBuild') : t('overviewTab.autoSlot')}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="cp-command-card">
        <div className="cp-panel-title-row">
          <div>
            <div className="settings-section-label">{t('overviewTab.connectedChannels')}</div>
            <h3>{channels.connected.length ? t('overviewTab.readyForGeneration') : t('overviewTab.connectChannelsWhenReady')}</h3>
          </div>
          <span className="chip chip-active">{t('overviewTab.connectedCount', { count: channels.connected.length })}</span>
        </div>
        <div className="cp-platform-mini-row">
          {channels.connected.map((row) => (
            <span key={row.platform} className="cp-platform-mini" title={row.name}>
              <PlatformIcon platform={row.platform} size={16} />
              {PLATFORM_LABELS[row.platform] || row.platform}
            </span>
          ))}
          {channels.available.slice(0, Math.max(1, 6 - channels.connected.length)).map((platform) => (
            <button key={platform} className="cp-platform-mini cp-platform-mini-add" onClick={() => onConnectPlatform?.(platform)} type="button">
              <Icon name="plus" size={12} />
              {t('overviewTab.addPlatform', { platform: PLATFORM_LABELS[platform] || platform })}
            </button>
          ))}
          {!channels.connected.length && !channels.available.length && <span className="caption muted">{t('overviewTab.noChannelsHint')}</span>}
        </div>
      </section>
    </div>
  );
}

function connectedChannelSummary(socialAccounts, platformPrefs) {
  const connected = [];
  const seen = new Set();
  (socialAccounts || []).forEach((account) => {
    const platform = normalizePlatform(account.platform);
    if (!platform || seen.has(platform) || account.status !== 'connected') return;
    seen.add(platform);
    connected.push({ platform, name: account.displayName || PLATFORM_LABELS[platform] || platform });
  });
  const preferred = (platformPrefs || []).filter(row => row.enabled).map(row => normalizePlatform(row.platform)).filter(Boolean);
  const pool = preferred.length ? preferred : PLATFORMS;
  return {
    connected,
    available: pool.filter(platform => !seen.has(platform)).slice(0, 6),
  };
}

function normalizePlatform(platform) {
  return platform === 'twitter' ? 'x' : platform;
}

function buildNextAction(items, strategy, t) {
  const firstMissing = items.find((item) => !item.ready && !item.autoReady);
  if (firstMissing) {
    return {
      label: t('overviewTab.nextBestAction'),
      title: t('overviewTab.needsAQuickCheck', { label: firstMissing.label }),
      cta: t('overviewTab.openLabel', { label: firstMissing.label }),
      tab: firstMissing.tab,
      icon: firstMissing.icon || 'arrow_r',
    };
  }
  return strategy
    ? { label: t('overviewTab.ready'), title: t('overviewTab.systemHasEnoughContext'), cta: t('overviewTab.buildMonth'), tab: 'month', icon: 'calendar' }
    : { label: t('overviewTab.ready'), title: t('overviewTab.brandInputsReady'), cta: t('overviewTab.generatePlan'), tab: 'plan', icon: 'target' };
}

function buildReadiness({ brandForm, publishingForm, platformPrefs, strategy, t }) {
  const items = [
    {
      id: 'brand',
      tab: 'brand',
      label: t('overviewTab.brandInputsLabel'),
      description: t('overviewTab.brandInputsDescription'),
      icon: 'sparkles',
      ready: !!brandForm?.businessDescription?.trim() && !!brandForm?.targetAudience?.trim(),
    },
    {
      id: 'plan',
      tab: 'plan',
      label: t('overviewTab.contentPlanLabel'),
      description: t('overviewTab.contentPlanDescription'),
      icon: 'target',
      ready: !!strategy,
    },
    {
      id: 'month',
      tab: 'month',
      label: t('overviewTab.monthPlanLabel'),
      description: t('overviewTab.postsPerWeekBaseline', { count: Number(publishingForm?.postsPerWeek) || 0 }),
      icon: 'calendar',
      ready: !!strategy && Number(publishingForm?.postsPerWeek) > 0,
    },
    {
      id: 'platforms',
      tab: 'plan',
      label: t('overviewTab.channelStyleLabel'),
      description: t('overviewTab.channelStyleDescription'),
      icon: 'layers',
      ready: platformPrefs.some((p) => p.enabled),
      autoReady: true,
      status: t('overviewTab.statusAuto'),
    },
  ];
  const score = Math.round((items.filter((x) => x.ready || x.autoReady).length / items.length) * 100);
  return { score, items };
}

function buildPlanSummary({ brandForm, publishingForm, platformPrefs, strategy, t }) {
  const inner = strategy?.strategy ?? {};
  if (inner.summary || strategy?.summary) return inner.summary || strategy.summary;
  const name = brandForm?.businessName || t('overviewTab.yourBrand');
  const cadence = publishingForm?.postsPerWeek ? t('overviewTab.postsPerWeek', { count: publishingForm.postsPerWeek }) : t('overviewTab.aSteadyCadence');
  const channels = platformPrefs.filter((p) => p.enabled).map((p) => PLATFORM_LABELS[p.platform] || p.platform);
  const channelText = channels.length ? channels.slice(0, 3).join(', ') : t('overviewTab.yourConnectedChannels');
  return t('overviewTab.planSummary', { name, cadence, channelText });
}
